
import { Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { Loader2 } from "lucide-react";

export const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, error } = useAuth();
  const location = useLocation();

  // Enhanced logging for authentication debugging
  useEffect(() => {
    console.log("[PrivateRoute] Auth state check:", { 
      path: location.pathname,
      loading, 
      authenticated: !!user, 
      userId: user?.id || "none", 
      hasError: !!error 
    });
  }, [user, loading, error, location.pathname]);

  // Show loader while checking authentication
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-heaven-gold mb-4" />
        <p className="text-lg text-gray-600">Verifying authentication...</p>
      </div>
    );
  }

  // Show error message if authentication failed
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-lg text-red-700 mb-4 font-bold">Authentication Error</div>
        <div className="mb-4">{error}</div>
        <a href="/sign-in" className="px-4 py-2 rounded-lg bg-heaven-gold text-white">
          Go to Sign In
        </a>
      </div>
    );
  }

  // Redirect to sign in if not authenticated
  if (!user) {
    console.log("[PrivateRoute] No authenticated user, redirecting to sign-in");
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};
